import React, { useState } from "react";
import { useCart } from "../context/cartcontext";
import { Link } from "react-router-dom";
import { notifyError, notifySuccess } from "../utils/notify";
import { emailConfigOk, sendOrderEmail } from "../utils/email";

const PRIMARY_COLOR = "text-teal-400";
const ACCENT_BG = "bg-teal-600 hover:bg-teal-500 transition-colors duration-200";

const Checkout = () => {
  const { cartItems, cartTotal, emptyCart } = useCart();
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash on Delivery");
  const [loading, setLoading] = useState(false);

  const handleOrder = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      notifyError("Cart is empty", "Add items before placing an order.");
      return;
    }

    setLoading(true);

    try {
      const orderId = Math.floor(Math.random() * 1000000); // mock order ID

      // Send email (frontend-only) via EmailJS if configured
      if (emailConfigOk) {
        await sendOrderEmail({
          orderId,
          customerName,
          customerEmail,
          customerPhone,
          shippingAddress,
          paymentMethod,
          items: cartItems,
          total: cartTotal,
        });
      }

      notifySuccess(
        `Order #${orderId} placed`,
        emailConfigOk
          ? "Confirmation sent to info@mehartraders.co.uk."
          : "Email not configured yet (EmailJS keys missing)."
      );

      emptyCart();
      setCustomerName("");
      setCustomerEmail("");
      setCustomerPhone("");
      setShippingAddress("");
      setPaymentMethod("Cash on Delivery");
    } catch (err) {
      notifyError(
        "Order placed, but email failed",
        "Please configure EmailJS settings to enable email notifications."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-gray-800 rounded-xl shadow-lg border border-teal-700 p-8">
        <h1 className={`text-4xl font-bold ${PRIMARY_COLOR} mb-8 border-b border-teal-600 pb-3`}>
          Checkout
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-4">Your cart is empty.</p>
            <Link
              to="/shop"
              className={`${ACCENT_BG} text-white px-6 py-3 rounded-full font-semibold`}
            >
              Go Back to Shop
            </Link>
          </div>
        ) : (
          <form onSubmit={handleOrder} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-teal-500 focus:border-teal-500"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-teal-500 focus:border-teal-500"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-2">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-teal-500 focus:border-teal-500"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                required
                placeholder="+44 7XXX XXX XXX"
              />
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-2">
                Shipping Address <span className="text-red-400">*</span>
              </label>
              <textarea
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-teal-500 focus:border-teal-500"
                rows="4"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">
                Payment Method
              </label>
              <select
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-teal-500 focus:border-teal-500"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option>Cash on Delivery</option>
                <option>Credit Card</option>
                <option>Bank Transfer</option>
              </select>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <div className="flex justify-between text-gray-300 mb-3">
                <span>Subtotal:</span>
                <span>£{cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white font-bold text-xl">
                <span>Total:</span>
                <span>£{cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full ${ACCENT_BG} text-white py-3 rounded-full text-lg font-semibold`}
            >
              {loading ? "Placing Order..." : "Place Order"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Checkout;
